import { AuthSession, getCurrentUser } from 'aws-amplify/auth';
import { LDClient, LDContext, initialize } from 'launchdarkly-react-client-sdk';
import kebabCase from 'lodash/kebabCase';

import { getSession } from './session';

let ldClient: LDClient | null = null;

const initLDClient = async (): Promise<LDClient> => {
  if (!ldClient) {
    const context = await getLaunchDarklyContextFromSession();
    const clientSideId = import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID;
    const deployEnv = import.meta.env.VITE_DEPLOY_ENV || 'unknown';

    console.log(`%c[LaunchDarkly] Initializing with environment: ${deployEnv}`, 'color: #405BFF; font-weight: bold', {
      context,
    });

    ldClient = initialize(clientSideId, context, { bootstrap: 'localStorage' });
    await ldClient.waitForInitialization();

    console.log(`%c[LaunchDarkly] Initialized successfully`, 'color: #405BFF; font-weight: bold');
  }
  return ldClient;
};

export async function getFlag(flagKey: string): Promise<boolean> {
  const client = await initLDClient();
  // Skip kebabCase if key already contains hyphens (avoids v2 → v-2 bug)
  const key = flagKey.includes('-') ? flagKey : kebabCase(flagKey);
  const value = client.variation(key, false);

  console.log(`%c[LaunchDarkly] Flag: ${key} = ${value}`, value ? 'color: #22c55e' : 'color: #ef4444');

  return value;
}

/**
 * Evaluates a flag using an anonymous LD context only — does not call getCurrentUser().
 * Use this before Cognito KeyValueStorage is configured (e.g. in aws.ts); getFlag() would
 * initialize LD with getCurrentUser() and read tokens from the wrong storage adapter.
 *
 * Note: targeting rules that depend on user attributes will not apply until the main LD client identifies the user.
 */
export async function getAnonymousBootstrapFlag(flagKey: string): Promise<boolean> {
  const clientSideId = import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID;
  if (!clientSideId || typeof window === 'undefined') {
    return false;
  }

  const key = flagKey.includes('-') ? flagKey : kebabCase(flagKey);
  const client = initialize(clientSideId, defaultContext, { bootstrap: 'localStorage' });
  try {
    await client.waitForInitialization();
    const value = client.variation(key, false);
    //console.log(`%c[LaunchDarkly] Bootstrap flag (anonymous): ${key} = ${value}`, value ? 'color: #22c55e' : 'color: #ef4444');
    return value;
  } finally {
    void client.close();
  }
}

const defaultContext: LDContext = {
  kind: 'user',
  anonymous: true,
};

export async function getLaunchDarklyContextFromSession(): Promise<LDContext> {
  let session: AuthSession;
  try {
    await getCurrentUser();
    session = await getSession();
  } catch {
    return defaultContext;
  }

  const userEmail = session.tokens?.idToken?.payload.email;
  const clientId = session.tokens?.idToken?.payload['custom:client_id'];
  const userId = session.tokens?.idToken?.payload['custom:user_id'];
  if (typeof userEmail !== 'string' || typeof clientId !== 'string') {
    return defaultContext;
  }
  return {
    kind: 'user',
    key: userEmail,
    email: userEmail,
    clientId,
    ...(typeof userId === 'string' ? { userId } : {}),
  };
}
